import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { MailOpen } from 'lucide-react'
import { Button, PasswordInput, setAccessToken, setRole, setUsername, Skeleton } from '../../core/index.js'
import { acceptInvitation, fetchInvitationPreview } from '../hooks.js'
import type { AcceptedSession, InvitationPreview } from '../types.js'

function AuthCard({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-marine-lt px-4">
      <div className="w-full max-w-sm bg-white border border-marine/15 p-8 shadow-sm">
        <div className="text-[9px] font-bold uppercase tracking-[2.5px] text-coral mb-1">autonoME</div>
        {children}
      </div>
    </div>
  )
}

const INPUT = 'w-full border border-marine/25 px-3 py-2 mb-4 text-sm text-ink focus:outline-none focus:border-marine'

function getTokenFromQuery(): string | null {
  return new URLSearchParams(window.location.search).get('token')
}

const KIND_WORDS: Record<InvitationPreview['kind'], string> = { admin: 'a SeeWhoCares admin', operator: 'an operator', facility_administrator: 'a facility administrator', worker: 'a member of staff' }

/** "as a facility administrator at North Campus, for Alpha Care". */
function describeAccess(preview: InvitationPreview): string {
  const facilities = preview.facilityNames.length > 0 ? ` at ${preview.facilityNames.join(' and ')}` : ''
  const organization = preview.organizationName ? ` for ${preview.organizationName}` : preview.kind === 'admin' ? ' of the SeeWhoCares platform' : ''
  return `as ${KIND_WORDS[preview.kind]}${facilities}${organization}`
}

export interface AcceptInvitationProps {
  /** The admin area signs in with the username and the password just set, since it sends its credential on every request; the other shells store the session the answer carries. */
  onAdminAccepted?: (credentials: { username: string; password: string }) => void
}

/**
 * The accept page (invitations plan 3.4, D11): opened from the link, it
 * shows who invited the person and to what, takes the password twice and
 * the name, and signs them in. Opened without a token, it asks for the
 * email and the six-digit code instead.
 */
export function AcceptInvitation({ onAdminAccepted }: AcceptInvitationProps) {
  const [token] = useState(getTokenFromQuery)
  const [preview, setPreview] = useState<InvitationPreview | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done'>('idle')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) {
      return
    }
    fetchInvitationPreview(token)
      .then((found) => {
        setPreview(found)
        setFirstName(found.firstName)
        setLastName(found.lastName)
      })
      .catch((err: unknown) => setPreviewError(err instanceof Error ? err.message : 'This invitation could not be read.'))
  }, [token])

  function land(session: AcceptedSession): void {
    if (session.kind === 'admin') {
      onAdminAccepted?.({ username: session.username, password })
      setStatus('done')
      return
    }
    setAccessToken(session.accessToken)
    setUsername(session.username)
    setRole(session.kind)
    window.location.replace(session.kind === 'worker' ? '/worker' : '/')
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (password !== confirm) {
      setError('Passwords do not match')
      return
    }
    setStatus('submitting')
    setError(null)
    try {
      const names = { ...(firstName.trim() ? { firstName: firstName.trim() } : {}), ...(lastName.trim() ? { lastName: lastName.trim() } : {}) }
      const session = await acceptInvitation(token ? { token, password, ...names } : { email: email.trim(), code: code.trim(), password, ...names })
      land(session)
    } catch (err) {
      setStatus('idle')
      setError(err instanceof Error ? err.message : 'The invitation could not be accepted')
    }
  }

  if (token && previewError) {
    return (
      <AuthCard>
        <h1 className="text-xl font-black text-ink mb-4">Invitation</h1>
        <p className="text-sm text-ink-mid">{previewError}</p>
      </AuthCard>
    )
  }
  if (token && preview && !preview.open) {
    return (
      <AuthCard>
        <h1 className="text-xl font-black text-ink mb-4">Invitation</h1>
        <p className="text-sm text-ink-mid">
          {preview.status === 'accepted' ? 'This invitation was already accepted. Sign in with your password.' : preview.status === 'expired' ? 'This invitation has expired. Ask to be invited again.' : 'This invitation was withdrawn.'}
        </p>
        <a href={preview.kind === 'admin' ? '/admin' : '/'} className="mt-4 inline-block text-sm font-bold text-marine hover:text-marine-dk">
          Go to sign in
        </a>
      </AuthCard>
    )
  }
  if (status === 'done') {
    return (
      <AuthCard>
        <h1 className="text-xl font-black text-ink mb-4">You're in</h1>
        <p className="text-sm text-ink-mid">Your password is set.</p>
      </AuthCard>
    )
  }

  return (
    <AuthCard>
      <form onSubmit={(event) => void handleSubmit(event)}>
        <h1 className="text-xl font-black text-ink mb-2">{preview ? `Welcome, ${preview.firstName}` : 'Accept your invitation'}</h1>
        {preview ? (
          <p className="text-sm text-ink-mid mb-6">
            {preview.invitedByName ?? preview.organizationName ?? 'SeeWhoCares'} invited you to autonoME {describeAccess(preview)}. Sent to {preview.email}. Set a password to get in.
          </p>
        ) : token ? (
          <Skeleton lines={5} label="Loading your invitation" className="mb-6" />
        ) : (
          <p className="text-sm text-ink-mid mb-6">Enter the email address your invitation went to and the six-digit code it carried, then set a password.</p>
        )}

        {!token && (
          <>
            <label className="block text-xs font-bold text-ink-mid mb-1" htmlFor="invite-email">
              Email
            </label>
            <input id="invite-email" type="email" className={INPUT} value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" required autoFocus />
            <label className="block text-xs font-bold text-ink-mid mb-1" htmlFor="invite-code">
              Code
            </label>
            <input id="invite-code" inputMode="numeric" pattern="\d{6}" maxLength={6} className={INPUT} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} placeholder="123456" required />
          </>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold text-ink-mid mb-1" htmlFor="invite-first">
              First name
            </label>
            <input id="invite-first" className={INPUT} value={firstName} onChange={(event) => setFirstName(event.target.value)} autoComplete="given-name" />
          </div>
          <div>
            <label className="block text-xs font-bold text-ink-mid mb-1" htmlFor="invite-last">
              Last name
            </label>
            <input id="invite-last" className={INPUT} value={lastName} onChange={(event) => setLastName(event.target.value)} autoComplete="family-name" />
          </div>
        </div>

        <label className="block text-xs font-bold text-ink-mid mb-1" htmlFor="invite-password">
          Password
        </label>
        <PasswordInput id="invite-password" className={INPUT} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" minLength={8} required autoFocus={Boolean(token)} />
        <label className="block text-xs font-bold text-ink-mid mb-1" htmlFor="invite-confirm">
          Confirm password
        </label>
        <PasswordInput id="invite-confirm" className={INPUT} value={confirm} onChange={(event) => setConfirm(event.target.value)} autoComplete="new-password" minLength={8} required />

        {error && <div className="text-coral text-xs font-bold mb-4">{error}</div>}

        <Button icon={MailOpen} size="lg" fullWidth type="submit" disabled={status === 'submitting' || (Boolean(token) && !preview)}>
          {status === 'submitting' ? 'Setting up…' : 'Set password and sign in'}
        </Button>
      </form>
    </AuthCard>
  )
}
